import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Joyride, { Step, Placement } from 'react-joyride';
import useWindowWidth from '../../../hooks/useWindowWidth';
import { joyrideStyles } from '../../../utils/joyrideStyles';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { updateCurrentUserInfo } from '../../../services/auth';

const HomeFeatureTourPart2: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const stepSelectors: (React.RefObject<HTMLElement> | string)[] = ['body'];

  const [showTour, setShowTour] = useState(false);
  const [stepIndex] = useState(0);
  const windowWidth = useWindowWidth();
  const placementsDesktop: Placement[] = ['center' as Placement];
  const placementsMobile: Placement[] = ['center' as Placement];

  const renderContents = useCallback((index: number) => {
    if (index === 0) {
      return (
        <>
          <div className="tour-wrapper_content">
            <h2>Great job!</h2>
            <p>Finish both QBank and flashcards to complete your session.</p>
          </div>
          <div
            className="tour-wrapper_footer"
            style={{ paddingBottom: '1rem' }}
          >
            <button
              className="button"
              onClick={() => {
                setShowTour(false);
                const floater = document.querySelector(
                  '.__floater'
                ) as HTMLDivElement;
                if (floater) {
                  floater.style.display = 'none';
                }
              }}
            >
              Got It!
            </button>
          </div>
        </>
      );
    }
  }, []);

  const handleSeenTour = useCallback(async () => {
    if (!currentUser) return;
    await updateCurrentUserInfo({
      onboardingTips: {
        ...currentUser?.onboardingTips,
        'home-tour-part-2': true,
      },
    });
  }, [currentUser]);

  useEffect(() => {
    setShowTour(true);
    handleSeenTour();
  }, []);

  const handleTourEnd = useCallback(() => {
    setShowTour(false);
  }, []);

  const steps = useMemo(() => {
    const areTargetsReady = stepSelectors.every((target) => {
      if (typeof target === 'string') {
        return true;
      } else {
        return target.current !== null;
      }
    });

    if (!areTargetsReady) return [];

    return stepSelectors.map((target, index) => {
      const stepTarget = typeof target === 'string' ? target : target.current!;
      return {
        target: stepTarget,
        placement:
          windowWidth > 900
            ? placementsDesktop[index]
            : placementsMobile[index],
        disableBeacon: true,
        content: (
          <div className="tour-wrapper tour-wrapper--flashcards">
            {renderContents(index)}
            <div className="blur-1"></div>
            <div className="blur-2"></div>
          </div>
        ),
      } as Step;
    });
  }, [stepSelectors, windowWidth, renderContents]);

  if (steps.length === 0) {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run={showTour}
      stepIndex={stepIndex}
      continuous
      hideCloseButton={true}
      hideBackButton={true}
      disableScrolling={true}
      disableOverlay
      styles={joyrideStyles}
      callback={({ status }) => {
        if (status === 'finished' || status === 'skipped') {
          handleTourEnd();
        }
      }}
    />
  );
};

export default HomeFeatureTourPart2;
